import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { useSirSetCurrencyExchangeRateMutation } from '../../../../graphql/react-operations';
import { Trans, useLingui } from '@lingui/react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';
import { GqlShareIssue, GqlShareIssueRegistration } from '../../../../graphql/base-types.graphql';

type FormValues = {
  currencyExchangeRate: string;
};

export const SiStepExchangeRate = ({
  shareIssue,
  shareIssueRegistration,
}: {
  shareIssue: Pick<GqlShareIssue, 'id' | 'currency' | 'companyId'>;
  shareIssueRegistration: Pick<GqlShareIssueRegistration, 'id' | 'currencyExchangeRate'>;
}) => {
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();
  const { i18n } = useLingui();

  const [doSave, { loading: loadingSave }] = useSirSetCurrencyExchangeRateMutation();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FormValues>();

  const onSubmit: SubmitHandler<FormValues> = async data => {
    if (!isDirty) {
      await stepNext();
    }
    if (!shareIssueRegistration) {
      throw new Error('missing shareIssueRegistration');
    }
    await doSave({
      variables: {
        companyId: shareIssue.companyId,
        input: { id: shareIssueRegistration.id, currencyExchangeRate: data.currencyExchangeRate },
      },
    });
    await stepNext();
    return null;
  };

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Trans id="Exchange Rate" />
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={3} maxWidth={300}>
          <Controller
            control={control}
            name="currencyExchangeRate"
            render={({ field }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="Exchange Rate" />
                </FormLabel>
                <NumericFormatInput
                  {...field}
                  defaultValue={shareIssueRegistration.currencyExchangeRate ?? undefined}
                  lang={i18n.locale}
                  decimalScale={4}
                  endDecorator={`SEK/${shareIssue.currency}`}
                  required
                />
              </FormControl>
            )}
          />
          {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

          <Box>
            <Button type="submit" loading={loadingSave}>
              <Trans id="Save and Continue" />
            </Button>
          </Box>
        </Stack>
      </form>
    </Stack>
  );
};
